<script setup lang="ts">
import { computed, type HTMLAttributes } from 'vue';

export type ResponsiveGridProps = {
  /** Defaults to 18em */
  colMinSize?: string;
  /** no default, columns can grow indefinitely */
  colMaxSize?: string;
  /**
   * Upper bound on the column count. No default: when omitted the grid is
   * uncapped and distributes the available space across as many `colMinSize`
   * tracks as fit.
   */
  maxCols?: number;
  /** Gap between columns */
  colGap: string;
  /** Defaults to `colGap` */
  rowGap?: string;
  /**
   * Keep the empty tracks (`auto-fill`) instead of collapsing them
   * (`auto-fit`): fewer items than columns keep the column width rather than
   * stretching across the row, so they stay on the lines of a neighbouring
   * grid with the same tracks (I-2833).
   */
  fill?: boolean;
};

const props = defineProps<ResponsiveGridProps & /* @vue-ignore */ HTMLAttributes>();

defineSlots<{
  default(): unknown;
}>();

/** A cap of 0 or less is meaningless — treat it like an omitted prop. */
const capped = computed(() => props.maxCols !== undefined && props.maxCols > 0);

const style = computed(() => {
  return {
    '--col-min': props.colMinSize ?? '18em',
    '--col-max': props.colMaxSize,
    '--col-cap': props.maxCols,
    '--col-gap': props.colGap,
    '--row-gap': props.rowGap ?? props.colGap,
    '--col-repeat': props.fill ? 'auto-fill' : 'auto-fit',
  };
});
</script>

<template>
  <div class="grid" :class="{ capped }" :style="style">
    <slot />
  </div>
</template>

<style scoped>
.grid {
  display: grid;
  /* Uncapped: as many --col-min tracks as fit, each growing to --col-max or 1fr. */
  grid-template-columns: repeat(
    var(--col-repeat, auto-fit),
    minmax(min(100%, var(--col-min)), var(--col-max, 1fr))
  );
  gap: var(--row-gap) var(--col-gap);
  align-items: start; /* fields keep natural height, no row stretch */
}

.grid.capped {
  /* Raise the track minimum so at most --col-cap tracks ever fit. */
  grid-template-columns: repeat(
    var(--col-repeat, auto-fit),
    minmax(
      min(
        100%,
        max(var(--col-min), (100% - (var(--col-cap) - 1) * var(--col-gap)) / var(--col-cap))
      ),
      var(--col-max, 1fr)
    )
  );
  /* Keep the count-limiter from forcing a track wider than --col-max
     (min>max in minmax would otherwise win). Unset --col-max => calc
     invalid => max-width: none => grid fills freely. */
  max-width: calc(var(--col-cap) * var(--col-max) + (var(--col-cap) - 1) * var(--col-gap));
}
</style>
